/**
 * 已有画笔图层缩放/调整尺寸时的同步预览
 * 复用 BrushCreator 的笔刷规则，以有界临时 Canvas 隔离描边合成，再按原图层顺序绘入舞台
 * 不生成或修改正式 resource；长画布离屏目标使用分块绘制，避免分配图层大小的临时位图
 */
import { getVectorTextFromCache } from '@/utils/file-kit';
import { BrushType, type IBrushLayerProps, type ILayerProps, type ISize } from '@/version-types';
import { drawBrushPreviewContent, getBrushResourceUrls } from '../utils/brush-creator';

/**
 * 判断是否可用同步交互预览，特殊图片变换和 AI 水印保留原资源路径
 * @param layer - 当前图层，素材必须已加载到正式渲染使用的缓存
 * @returns 是否可以在不改变笔刷样式的情况下同步重绘
 */
export function canPreviewBrushInteraction(layer: ILayerProps) {
    const data = layer.data as IBrushLayerProps;
    if (layer.__renderAiWatermark || !data.fills?.length || layer.width <= 0 || layer.height <= 0) return false;
    const transform = data.imageTransform;
    if (
        transform &&
        (transform.flipH ||
            transform.flipV ||
            transform.rotate ||
            transform.offsetX ||
            transform.offsetY ||
            transform.scale !== 1 ||
            transform.widthRatio !== 1 ||
            transform.heightRatio !== 1)
    )
        return false;
    const urls = getBrushResourceUrls(data);
    if (urls.some((url) => url && !getVectorTextFromCache(url))) return false;
    return (
        data.brushType === BrushType.Rect ||
        data.brushType === BrushType.Ellipse ||
        data.brushType === BrushType.Arrow ||
        !!(data.__drawPath || data.url || data.brushType === BrushType.Line)
    );
}

/** 保存单张可复用的隔离画布，只在交互期间分配像素内存 */
export class BrushInteractionPreview {
    private canvas: HTMLCanvasElement | null = null;

    /** 释放临时像素缓冲；不影响正式资源 */
    clear() {
        if (this.canvas) {
            this.canvas.width = 0;
            this.canvas.height = 0;
            this.canvas = null;
        }
    }

    /**
     * 按目标上下文已有的旋转、平移、裁剪和透明度合成交互笔迹
     * @param target - 舞台或 Frame 的上下文，函数会写入当前图层的像素
     * @param layer - 当前帧最新的完整笔刷配置
     * @param maxSize - 单块临时画布的最大物理像素尺寸，通常为视口尺寸乘 DPR
     * @returns 是否完成绘制；无法绘制时由调用方回退已有资源
     */
    render(target: CanvasRenderingContext2D, layer: ILayerProps, maxSize: ISize) {
        if (
            !Number.isFinite(maxSize.width) ||
            !Number.isFinite(maxSize.height) ||
            maxSize.width <= 0 ||
            maxSize.height <= 0
        )
            return false;
        const matrix = target.getTransform();
        const { width, height } = layer;
        const points = [
            [0, 0],
            [width, 0],
            [0, height],
            [width, height],
        ].map(([x, y]) => ({
            x: matrix.a * x + matrix.c * y + matrix.e,
            y: matrix.b * x + matrix.d * y + matrix.f,
        }));
        const left = Math.max(0, Math.floor(Math.min(...points.map((p) => p.x))));
        const top = Math.max(0, Math.floor(Math.min(...points.map((p) => p.y))));
        const right = Math.min(target.canvas.width, Math.ceil(Math.max(...points.map((p) => p.x))));
        const bottom = Math.min(target.canvas.height, Math.ceil(Math.max(...points.map((p) => p.y))));
        if (right <= left || bottom <= top) return true;
        // 缓冲尺寸只随目标/视口变化，不能随每一帧的图层包围盒变化而反复重新分配。
        const tileWidth = Math.max(1, Math.min(target.canvas.width, Math.ceil(maxSize.width)));
        const tileHeight = Math.max(1, Math.min(target.canvas.height, Math.ceil(maxSize.height)));
        if (!Number.isFinite(tileWidth) || !Number.isFinite(tileHeight)) return false;
        this.canvas ||= document.createElement('canvas');
        const canvas = this.canvas;
        const ctx = canvas.getContext('2d');
        if (!ctx) return false;
        if (canvas.width !== tileWidth) canvas.width = tileWidth;
        if (canvas.height !== tileHeight) canvas.height = tileHeight;

        for (let y = top; y < bottom; y += tileHeight) {
            for (let x = left; x < right; x += tileWidth) {
                ctx.setTransform(1, 0, 0, 1, 0, 0);
                ctx.clearRect(0, 0, tileWidth, tileHeight);
                ctx.save();
                let painted = false;
                try {
                    ctx.setTransform(matrix.a, matrix.b, matrix.c, matrix.d, matrix.e - x, matrix.f - y);
                    ctx.beginPath();
                    ctx.rect(0, 0, width, height);
                    ctx.clip();
                    // 必须在透明的隔离画布上执行 destination-out，不能擦除舞台中已有的图层。
                    painted = drawBrushPreviewContent(ctx, layer);
                } finally {
                    ctx.restore();
                }
                if (!painted) return false;
                target.save();
                try {
                    target.setTransform(1, 0, 0, 1, 0, 0);
                    const drawWidth = Math.min(tileWidth, right - x);
                    const drawHeight = Math.min(tileHeight, bottom - y);
                    // target 的裁剪和整体透明度保持不变，透明度只在最终合成时应用一次。
                    target.drawImage(canvas, 0, 0, drawWidth, drawHeight, x, y, drawWidth, drawHeight);
                } finally {
                    target.restore();
                }
            }
        }
        return true;
    }
}
